import React, { useState } from 'react';
import { nip19 } from 'nostr-tools';
import { useParams, useNavigate } from 'react-router-dom';
import NotFound from './NotFound';
import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@/hooks/useNostr';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { useFollow } from '@/hooks/useFollow';
import { useFollowingList } from '@/hooks/useFollowingList';
import { useFollowersList } from '@/hooks/useFollowersList';
import { UserListSheet } from '@/components/profile/UserListSheet';
import { ImageViewer } from '@/components/ui/ImageViewer';
import { Tweet } from '@/components/tweet/Tweet';
import { ComposeBox } from '@/components/tweet/ComposeBox';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Calendar, Link as LinkIcon, BadgeCheck, Camera, Copy, Check, QrCode, Mail } from 'lucide-react';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { QrScannerView, useQrScanner, parseScanResult } from '@/components/ui/QrScanner';

export function NIP19Page() {
  const { nip19: identifier } = useParams<{ nip19: string }>();
  const navigate = useNavigate();

  if (!identifier) {
    return <NotFound />;
  }

  let decoded;
  try {
    decoded = nip19.decode(identifier);
  } catch {
    return <NotFound />;
  }

  const { type, data } = decoded;

  switch (type) {
    case 'npub':
    case 'nprofile':
      const pubkey = type === 'npub' ? (data as string) : (data as nip19.ProfilePointer).pubkey;
      return <ProfileView pubkey={pubkey} />;

    case 'note':
    case 'nevent':
      const noteId = type === 'note' ? (data as string) : (data as nip19.EventPointer).id;
      return <ThreadView entryNoteId={noteId} />;

    default:
      return <NotFound />;
  }
}

/* ================= PROFILE VIEW COMPONENT ================= */
function ProfileView({ pubkey }: { pubkey: string }) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutate: publish } = useNostrPublish();
  const { toast } = useToast();
  const navigate = useNavigate();
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const npub = nip19.npubEncode(pubkey);

  const [activeTab, setActiveTab] = useState<'posts' | 'replies' | 'likes'>('posts');
  const [copied, setCopied] = useState(false);
  const [isQrOpen, setIsQrOpen] = useState(false);
  const { scanState, setScanState, errorMsg, setErrorMsg, result: scanResult, setResult: setScanResult, reset: resetScanner } = useQrScanner();

  // Followers / Following list state
  const [followSheetOpen, setFollowSheetOpen] = useState(false);
  const [followSheetType, setFollowSheetType] = useState<'following' | 'followers'>('following');
  const [viewerSrc, setViewerSrc] = useState<string | null>(null);
  const { pubkeys: followingPubkeys, isLoading: followingListLoading } = useFollowingList(pubkey);
  const { pubkeys: followersPubkeys, isLoading: followersListLoading } = useFollowersList(pubkey);

  const handleCopyNpub = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(npub);
    setCopied(true);
    toast({
      title: 'Copied npub 📋',
      description: 'Your Nostr pubkey string is copied to clipboard.'
    });
    setTimeout(() => setCopied(false), 1500);
  };

  const isMe = user?.pubkey === pubkey;

  // Follow/unfollow hook
  const { isFollowing, isLoading: followLoading, toggleFollow } = useFollow(pubkey);

  // Retrieve user events (kind 1)
  const { data: userEvents = [], isLoading: isEventsLoading } = useQuery({
    queryKey: ['profile-events', pubkey, activeTab],
    queryFn: async () => {
      if (!nostr) return [];
      if (activeTab === 'posts') {
        const notes = await nostr.query([{ kinds: [1], authors: [pubkey], limit: 40 }]);
        // Filter out replies manually for "Posts" tab
        return notes.filter(n => !n.tags.some(t => t[0] === 'e'));
      } else if (activeTab === 'replies') {
        const notes = await nostr.query([{ kinds: [1], authors: [pubkey], limit: 40 }]);
        return notes.filter(n => n.tags.some(t => t[0] === 'e'));
      } else {
        // Find kind 7 (reactions) made by user
        const likes = await nostr.query([{ kinds: [7], authors: [pubkey], limit: 20 }]);
        const likedIds = likes.map(l => l.tags.find(t => t[0] === 'e')?.[1]).filter((id): id is string => !!id);
        if (likedIds.length === 0) return [];
        return nostr.query([{ kinds: [1], ids: likedIds }]);
      }
    },
    enabled: !!nostr,
  });

  // NIP-02: Following count from user's own kind 3 contact list
  const { data: followingCount } = useQuery({
    queryKey: ['following-count', pubkey],
    queryFn: async () => {
      if (!nostr) return 0;
      const events = await nostr.query([{ kinds: [3], authors: [pubkey], limit: 1 }]);
      if (events[0]) {
        return events[0].tags.filter(t => t[0] === 'p').length;
      }
      return 0;
    },
    enabled: !!nostr,
  });

  // NIP-02: Followers count from other users' kind 3 events that p-tag the profile user
  const { data: followersCount } = useQuery({
    queryKey: ['followers-count', pubkey],
    queryFn: async () => {
      if (!nostr) return 0;
      const events = await nostr.query([{ kinds: [3], '#p': [pubkey] }]);
      return events.length;
    },
    enabled: !!nostr,
    staleTime: 300000, // 5 minute cache
  });

  return (
    <div className="flex flex-col w-full min-h-screen bg-background text-foreground">
      {/* Header element */}
      <div className="sticky top-0 bg-background/85 backdrop-blur-md z-30 border-b border-border flex items-center gap-6 px-4 h-14 shrink-0 select-none">
        <button onClick={() => window.history.back()} className="p-2 hover:bg-secondary rounded-full transition-colors cursor-pointer focus-visible:outline-sky-500">
          <ArrowLeft className="size-5" />
        </button>
        <div className="flex-1 min-w-0 flex flex-col leading-tight truncate">
          <span className="font-extrabold text-lg flex items-center gap-1 leading-snug">
            {metadata?.display_name || metadata?.name || 'Anonymous'}
            <BadgeCheck className="size-4.5 text-sky-500 fill-sky-500 shrink-0" />
          </span>
          <button 
            type="button"
            onClick={handleCopyNpub}
            className="text-xs text-muted-foreground hover:text-sky-500 transition-colors flex items-center gap-1 cursor-pointer truncate font-mono bg-transparent border-none p-0 text-left"
            title="Copy npub"
          >
            <span className="truncate">@{npub.slice(0, 12)}...{npub.slice(-8)}</span>
            {copied ? (
              <Check className="size-3 text-teal-500 stroke-[2.5]" />
            ) : (
              <Copy className="size-3" />
            )}
          </button>
        </div>
      </div>

      {/* Banner & Cover Photo */}
      <div className="relative w-full aspect-3/1 bg-secondary border-b border-border/80 overflow-hidden">
        {metadata?.banner ? (
          <img src={metadata.banner} alt="User Profile Banner" className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity" onClick={(e) => { e.stopPropagation(); if (metadata?.banner) setViewerSrc(metadata.banner); }} />
        ) : (
          <div className="w-full h-full bg-slate-500/10 flex items-center justify-center">
            <span className="text-xs text-muted-foreground font-semibold">Decentralized Nostr banner space</span>
          </div>
        )}
      </div>

      {/* Profile Metrics and Info detail card */}
      <div className="px-4 pb-4 flex flex-col gap-3 relative border-b border-border/60">
        {/* Avatar overlay - Restructured with mt-12 on avatar only, keeping buttons in normal flow to prevent overlay clipping */}
        <div className="flex justify-between items-start relative z-10 select-none">
          <img 
            src={metadata?.picture || `https://robohash.org/${pubkey}.png?set=set4`}
            alt="Profile overlay pic" 
            className="size-24 rounded-full border-4 border-background object-cover bg-neutral-200 -mt-12 relative z-10 cursor-pointer hover:opacity-90 transition-opacity"
            onClick={(e) => { e.stopPropagation(); if (metadata?.picture) setViewerSrc(metadata.picture); }}
          />

          <div className="flex items-center gap-2 mt-3 relative z-20">
            {/* Direct Message Chat Button on counterpart profiles (hide if isMe is true) */}
            {!isMe && (
              <button
                type="button"
                onClick={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  navigate(`/chat/${npub}`);
                }}
                className="rounded-full size-9 p-0 bg-foreground text-background hover:bg-foreground/90 flex items-center justify-center shrink-0 cursor-pointer focus-visible:outline-sky-500 shadow-sm transition-all"
                title={`Message ${metadata?.display_name || metadata?.name || 'User'}`}
              >
                <Mail className="size-4.5 shrink-0" />
              </button>
            )}

            <Button 
              type="button"
              className={`rounded-full font-bold font-sans text-sm h-9 px-4 cursor-pointer shrink-0 shadow-sm transition-all ${
                isMe 
                  ? 'bg-foreground text-background hover:bg-foreground/90' 
                  : isFollowing 
                    ? 'bg-muted text-foreground hover:bg-muted/80' 
                    : 'bg-foreground text-background hover:bg-foreground/90'
              }`}
              disabled={followLoading || !user}
              onClick={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                if (isMe) {
                  toast({ title: 'Edit Bio', description: 'To fully customize and edit your brand profile, update custom kind 0 metadata in Settings page.' });
                } else if (user) {
                  toggleFollow();
                }
              }}
            >
              {isMe ? 'Edit profile' : (isFollowing ? 'Following' : 'Follow')}
            </Button>
          </div>
        </div>

        {/* Brand details */}
        <div className="flex flex-col select-none -mt-3 gap-0.5">
          <h2 className="font-black text-xl flex items-center gap-1 leading-none">
            {metadata?.display_name || metadata?.name || 'Anonymous'}
            <BadgeCheck className="size-5 text-sky-500 fill-sky-500 shrink-0" />
          </h2>
          
          <div className="flex items-center gap-1.5 mt-0.5 select-none">
            {/* Click-to-copy npub handle button */}
            <button 
              onClick={handleCopyNpub}
              className="text-sm font-normal text-muted-foreground hover:text-sky-500 transition-colors flex items-center gap-1 cursor-pointer focus-visible:outline-sky-500 rounded-md py-0.5 px-1 -ml-1"
              title="Copy full npub keys"
            >
              <span>@{npub.slice(0, 10)}...{npub.slice(-6)}</span>
              {copied ? (
                <Check className="size-3.5 text-teal-500 shrink-0 stroke-[2.5]" />
              ) : (
                <Copy className="size-3.5 text-muted-foreground shrink-0" />
              )}
            </button>

            {/* Click-to-QR popup helper */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setIsQrOpen(true);
              }}
              className="p-1 rounded-full text-muted-foreground hover:text-sky-500 hover:bg-sky-500/10 transition-all cursor-pointer focus-visible:outline-sky-500"
              title="Show QR payload code"
            >
              <QrCode className="size-3.5 shrink-0" />
            </button>
          </div>
        </div>

        {metadata?.about && (
          <p className="text-[15px] leading-relaxed break-words">{metadata.about}</p>
        )}

        {/* Bio links */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-muted-foreground mt-1">
          {metadata?.website && (
            <a 
              href={metadata.website} 
              target="_blank" 
              rel="noopener" 
              className="flex items-center gap-1 text-sky-500 hover:underline"
            >
              <LinkIcon className="size-3.5" />
              <span className="max-w-[200px] truncate">{metadata.website.replace(/^https?:\/\/(www\.)?/, '')}</span>
            </a>
          )}
          {metadata?.nip05 && (
            <span className="flex items-center gap-1 text-teal-600 font-medium">
              <BadgeCheck className="size-3.5 text-teal-500 fill-teal-500" />
              <span>{metadata.nip05}</span>
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="size-3.5" />
            <span>Decentralized node verify</span>
          </span>
        </div>

        {/* Stats segment */}
        <div className="flex items-center gap-5 text-sm mt-1">
          <div
            className="hover:underline cursor-pointer"
            onClick={() => { setFollowSheetType('following'); setFollowSheetOpen(true); }}
          >
            <span className="font-extrabold text-foreground">
              {followingCount !== undefined ? followingCount : '…'}{' '}
            </span>
            <span className="text-muted-foreground">Following</span>
          </div>
          <div
            className="hover:underline cursor-pointer"
            onClick={() => { setFollowSheetType('followers'); setFollowSheetOpen(true); }}
          >
            <span className="font-extrabold text-foreground">
              {followersCount !== undefined ? followersCount : '…'}{' '}
            </span>
            <span className="text-muted-foreground">Followers</span>
          </div>
        </div>
      </div>

      {/* QR Code Scannable Dialog Modal */}
      <Dialog open={isQrOpen} onOpenChange={(open) => {
        if (!open) {
          resetScanner();
        }
        setIsQrOpen(open);
      }}>
        <DialogContent className="sm:max-w-[340px] rounded-2xl p-5 border border-border flex flex-col items-center text-center">
          <DialogTitle className="font-extrabold text-[16px] text-foreground self-start pl-1 sm:pl-0 leading-none">
            {scanState === 'idle' || scanState === 'error' ? 'User QR Profile' : 'Scan QR Code'}
          </DialogTitle>

          <div className="flex flex-col items-center gap-4 mt-3 w-full">
            {scanState === 'scanning' || scanState === 'requesting' || scanState === 'decoded' ? (
              /* ================= SCANNER VIEW ================= */
              <QrScannerView
                onResult={(result) => {
                  const npub = result.type === 'npub' ? result.value : result.identifier;
                  setIsQrOpen(false);
                  resetScanner();
                  navigate(`/${npub}`);
                }}
                onError={(msg) => setErrorMsg(msg)}
                onBack={() => resetScanner()}
                scanState={scanState}
                setScanState={setScanState}
              />
            ) : (
              /* ================= QR DISPLAY VIEW ================= */
              <>
                {/* Header Identity Display overlay */}
                <div className="flex items-center gap-3 w-full border-b border-border/50 pb-3 justify-center">
                  <img 
                    src={metadata?.picture || `https://robohash.org/${pubkey}.png?set=set4`}
                    alt="Profile Avatar" 
                    className="size-11 rounded-full object-cover border border-border bg-secondary shrink-0"
                  />
                  <div className="flex flex-col text-left leading-normal max-w-[170px] truncate select-none">
                    <span className="font-extrabold text-sm truncate">{metadata?.display_name || metadata?.name || 'Anonymous'}</span>
                    <span className="text-xs text-muted-foreground shrink-0 leading-none">Scan to find this profile</span>
                  </div>
                </div>

                {/* Light canvas panel for robust code scanner captures */}
                <div className="p-3 bg-white rounded-2xl shadow-xs border border-neutral-100 flex items-center justify-center">
                  <QRCodeCanvas value={`nostr:${npub}`} size={200} level="M" />
                </div>

                {/* Scan QR Code Button */}
                <button 
                  type="button"
                  onClick={() => setScanState('requesting')}
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full border border-dashed border-border hover:bg-secondary/30 transition-colors text-sm font-medium text-muted-foreground hover:text-foreground cursor-pointer"
                >
                  <Camera className="size-4" />
                  <span>Scan a QR code</span>
                </button>

                {/* Monospace keys display with tap-to-copy button */}
                <button 
                  onClick={handleCopyNpub}
                  className="flex items-center justify-between w-full p-2.5 rounded-xl border border-border/80 hover:bg-secondary/30 transition-colors font-mono text-[11px] text-muted-foreground break-all text-left bg-secondary/15 select-all focus-visible:outline-sky-500 cursor-pointer"
                  title="Copy keys"
                >
                  <span className="max-w-[210px] break-all leading-normal select-all">{npub}</span>
                  {copied ? (
                    <Check className="size-4 text-teal-600 shrink-0 select-none stroke-[2.5]" />
                  ) : (
                    <Copy className="size-4 text-muted-foreground shrink-0 select-none" />
                  )}
                </button>
                
                {errorMsg && (
                  <p className="text-[11px] text-rose-500 font-medium text-center leading-normal">{errorMsg}</p>
                )}
                
                <p className="text-[11px] text-muted-foreground leading-normal mt-0.5 select-none">
                  NIP-21 compliant. Scan leveraging any primary client app to seamlessly connect, reply or message!
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile sub-tab list feeds */}
      <Tabs defaultValue="posts" className="w-full mt-1 border-b border-border/75" onValueChange={(val) => setActiveTab(val as any)}>
        <TabsList className="w-full flex h-13 bg-transparent p-0 justify-center">
          <TabsTrigger value="posts" className="flex-1 h-full text-[15px] font-bold rounded-none border-b-4 border-transparent data-[state=active]:border-sky-500 data-[state=active]:text-foreground text-muted-foreground/80 cursor-pointer hover:bg-secondary/20">
            Posts
          </TabsTrigger>
          <TabsTrigger value="replies" className="flex-1 h-full text-[15px] font-bold rounded-none border-b-4 border-transparent data-[state=active]:border-sky-500 data-[state=active]:text-foreground text-muted-foreground/80 cursor-pointer hover:bg-secondary/20">
            Replies
          </TabsTrigger>
          <TabsTrigger value="likes" className="flex-1 h-full text-[15px] font-bold rounded-none border-b-4 border-transparent data-[state=active]:border-sky-500 data-[state=active]:text-foreground text-muted-foreground/80 cursor-pointer hover:bg-secondary/20">
            Likes
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Feed Area */}
      <div className="flex flex-col w-full divide-y divide-border">
        {isEventsLoading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="p-4 flex gap-3 border-b border-border animate-pulse">
              <Skeleton className="size-10 rounded-full shrink-0" />
              <div className="flex-1 flex flex-col gap-2">
                <Skeleton className="h-4 w-28 rounded-md" />
                <Skeleton className="h-4 w-full rounded-md" />
              </div>
            </div>
          ))
        ) : userEvents.length > 0 ? (
          userEvents.map((event) => (
            <Tweet key={event.id} event={event} />
          ))
        ) : (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No items here yet. Be the first to start a conversation!
          </div>
        )}
      </div>

      {/* Followers / Following list sheet */}
      <UserListSheet
        open={followSheetOpen}
        onOpenChange={setFollowSheetOpen}
        title={followSheetType === 'following' ? 'Following' : 'Followers'}
        pubkeys={followSheetType === 'following' ? followingPubkeys : followersPubkeys}
        isLoading={followSheetType === 'following' ? followingListLoading : followersListLoading}
      />

      {/* Image viewer lightbox */}
      <ImageViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />
    </div>
  );
}

/* ================= THREAD / NOTE VIEW COMPONENT ================= */
function ThreadView({ entryNoteId }: { entryNoteId: string }) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();

  // Retrieve details of the focused event
  const { data: focusedEvent, isLoading: isFocusedLoading } = useQuery({
    queryKey: ['note', entryNoteId],
    queryFn: async () => {
      if (!nostr) return null;
      const notes = await nostr.query([{ kinds: [1], ids: [entryNoteId] }]);
      return notes[0] || null;
    },
    enabled: !!nostr,
  });

  // Query parent note if it exists (using e tags)
  const parentId = focusedEvent?.tags.find(t => t[0] === 'e')?.[1];

  const { data: parentEvent } = useQuery({
    queryKey: ['note', parentId],
    queryFn: async () => {
      if (!nostr || !parentId) return null;
      const notes = await nostr.query([{ kinds: [1], ids: [parentId] }]);
      return notes[0] || null;
    },
    enabled: !!nostr && !!parentId,
  });

  // Query replies to target note id
  const { data: replies = [], refetch: refetchReplies, isLoading: isRepliesLoading } = useQuery({
    queryKey: ['replies', entryNoteId],
    queryFn: async () => {
      if (!nostr) return [];
      return nostr.query([{ kinds: [1], '#e': [entryNoteId] }]);
    },
    enabled: !!nostr,
  });

  if (isFocusedLoading) {
    return (
      <div className="p-6 flex flex-col gap-4 animate-pulse bg-background">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!focusedEvent) {
    return <NotFound />;
  }

  const handleReplySuccess = () => {
    refetchReplies();
  };

  return (
    <div className="flex flex-col w-full min-h-screen bg-background">
      {/* Thread Header bar */}
      <div className="sticky top-0 bg-background/85 backdrop-blur-md z-30 border-b border-border flex items-center gap-6 px-4 h-14">
        <button onClick={() => window.history.back()} className="p-2 hover:bg-secondary rounded-full transition-colors cursor-pointer">
          <ArrowLeft className="size-5" />
        </button>
        <span className="font-extrabold text-lg text-foreground">Post Thread</span>
      </div>

      <div className="flex flex-col">
        {/* Render Ancestor / Parent thread row */}
        {parentEvent && (
          <Tweet event={parentEvent} isReplyThread={true} />
        )}

        {/* Focused primary Tweet */}
        <div className="border-b border-border/80 bg-secondary/5">
          <Tweet event={focusedEvent} />
        </div>

        {/* Compact reply input composer box */}
        {user && (
          <div className="p-4 border-b border-border">
            <span className="text-[13px] text-muted-foreground block mb-2">
              Compose reply to <span className="font-bold">@{nip19.npubEncode(focusedEvent.pubkey).slice(0, 10)}...</span>
            </span>
            <ComposeBox 
              replyToEventId={focusedEvent.id} 
              replyToPubkey={focusedEvent.pubkey} 
              placeholder="Post your reply"
              onPostSuccess={handleReplySuccess}
            />
          </div>
        )}

        {/* Inline Thread Replies list */}
        <div className="flex flex-col w-full divide-y divide-border">
          {replies.length > 0 ? (
            replies.map((reply) => (
              <Tweet key={reply.id} event={reply} />
            ))
          ) : (
            <div className="p-8 text-center text-muted-foreground text-sm">
              Be the first to share your thoughts on this conversation!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
